// One-time migration of a localStorage-only song library into the
// cloud `patches` table. Runs on the first SIGNED_IN event after the
// cloud-first refactor ships, then sets a marker so it never runs
// again for that user-on-this-device.
//
// Strategy: insert every local song that doesn't already have a
// matching cloud row (matched by `cloudPatchId`). Songs that were
// previously published keep their cloudPatchId; songs that were
// local-only get a fresh cloud id.
//
// We don't UPDATE cloud rows from local data — if a song was
// previously published and the cloud row has since been edited (by
// another device or the MCP), the cloud is the authoritative version.
// Local-only edits since last publish are lost in that case; this is
// the price of treating cloud as source-of-truth.

import { getSupabase } from '../auth/client';
import { loadAll } from '../storage';
import type { Song } from '../types';

const MIGRATION_MARKER_KEY = 'strudel-harness:migration:v1-to-cloud:done';
const MIGRATED_BACKUP_KEY_PREFIX = 'strudel-harness:songs:v1:migrated-backup';

export type MigrationResult = {
  inserted: number;
  skipped: number;
  alreadyDone: boolean;
};

export async function migrateLocalLibraryIfNeeded(
  userId: string,
): Promise<MigrationResult> {
  const markerKey = `${MIGRATION_MARKER_KEY}:${userId}`;
  if (localStorage.getItem(markerKey) === '1') {
    return { inserted: 0, skipped: 0, alreadyDone: true };
  }

  const local = loadAll();
  if (!local || local.length === 0) {
    localStorage.setItem(markerKey, '1');
    return { inserted: 0, skipped: 0, alreadyDone: false };
  }

  const supabase = getSupabase();
  if (!supabase) {
    // Cloud unavailable — defer the migration. Don't set the marker;
    // we'll retry on next sign-in.
    return { inserted: 0, skipped: local.length, alreadyDone: false };
  }

  // Check which already-cloud-attached local songs still resolve
  // server-side. Anything missing gets re-inserted with the same
  // cloudPatchId IS NOT supported (Supabase generates ids on insert);
  // those songs become fresh inserts and the local cloudPatchId is
  // discarded. The local row is rewritten to point at the new cloud id.
  const knownCloudIds = local.flatMap((s) => (s.cloudPatchId ? [s.cloudPatchId] : []));
  const existingIds = new Set<string>();
  if (knownCloudIds.length > 0) {
    const { data, error } = await supabase
      .from('patches')
      .select('id')
      .in('id', knownCloudIds)
      .eq('owner_id', userId);
    if (error) {
      throw new Error(`migration lookup failed: ${error.message}`);
    }
    for (const row of data ?? []) existingIds.add(row.id as string);
  }

  let inserted = 0;
  let skipped = 0;
  for (const song of local) {
    if (song.cloudPatchId && existingIds.has(song.cloudPatchId)) {
      skipped++;
      continue;
    }
    const { error } = await supabase.from('patches').insert({
      owner_id: userId,
      title: (song.title || 'Untitled').slice(0, 200),
      code: song.code,
      visibility: 'unlisted',
    });
    if (error) {
      throw new Error(`migration insert failed for "${song.title}": ${error.message}`);
    }
    inserted++;
  }

  // Stash a one-time backup of the local data, scoped by uid so that
  // a second account on the same browser doesn't clobber the first
  // account's recovery snapshot. The active `:v1` key is shared, so
  // we only clear it once the backup is in place.
  try {
    localStorage.setItem(
      `${MIGRATED_BACKUP_KEY_PREFIX}:${userId}`,
      JSON.stringify(local),
    );
    localStorage.removeItem('strudel-harness:songs:v1');
  } catch {
    // Best-effort; the marker still gets set.
  }
  localStorage.setItem(markerKey, '1');

  return { inserted, skipped, alreadyDone: false };
}

/** Returns the local-only songs that haven't been migrated yet —
 *  used by the anon-with-local-data banner to count what's at risk
 *  if the user doesn't sign in. Reads localStorage directly; no
 *  side effects. */
export function getUnmigratedLocalSongs(): Song[] {
  return loadAll() ?? [];
}
